import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  createdAt: Date;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    createdAt: new Date(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'applications'), {
        ...formData,
        createdAt: new Date()
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', phone: '', message: '', createdAt: new Date() });
      
      // Opcionális: küldjünk értesítő emailt magunknak
      // Ezt később implementálhatjuk Firebase Cloud Functions segítségével
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // 3 másodperc múlva töröljük az állapotüzenetet
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      {/* Hero section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-800 rounded-xl shadow-lg mb-12 p-8 text-white">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Kapcsolat
        </h1>
        <p className="text-xl">
          Segítünk a NIS2 megfelelőség elérésében
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* Kapcsolati információk */}
        <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl shadow-lg p-8 border border-purple-200">
          <h2 className="text-2xl font-bold mb-6 text-purple-900">Elérhetőségeink</h2>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900">Kapcsolattartó</h3>
                <p className="text-purple-700">Nagy Roland</p>
                <p className="text-sm text-purple-600">NIS2 Megfelelőségi Tanácsadó</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900">E-mail</h3>
                <a href="mailto:nagyroland@webforyou.hu" className="text-purple-700 hover:text-purple-900 transition-colors">
                  nagyroland@webforyou.hu
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900">Telefon</h3>
                <a href="tel:+36709083086" className="text-purple-700 hover:text-purple-900 transition-colors">
                  +36 70 908 3086
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900">Cég</h3>
                <p className="text-purple-700">Webforyou Kft.</p>
              </div>
            </div>
          </div>
        </div>

        {/* NIS2 információk */}
        <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl shadow-lg p-8 border border-purple-200">
          <h2 className="text-2xl font-bold mb-6 text-purple-900">NIS2 információk</h2>
          
          <div className="space-y-4">
            <Link 
              to="/tudnivalok"
              className="block p-4 bg-white/50 rounded-lg hover:bg-white/80 transition-colors"
            >
              <h3 className="text-lg font-semibold text-purple-900 mb-2">NIS2 Megfelelőségi Tudnivalók</h3>
              <p className="text-purple-700">Részletes információk a NIS2 irányelvről és a megfelelőségi követelményekről</p>
            </Link>

            <Link 
              to="/nis2"
              className="block p-4 bg-white/50 rounded-lg hover:bg-white/80 transition-colors"
            >
              <h3 className="text-lg font-semibold text-purple-900 mb-2">NIS2 Tanácsadás</h3>
              <p className="text-purple-700">Ismerje meg NIS2 megfelelőségi tanácsadási szolgáltatásainkat</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Kapcsolatfelvételi űrlap */}
      <div className="bg-gradient-to-r from-purple-800 to-indigo-900 rounded-xl shadow-lg p-8 text-white text-center mb-12">
        <h2 className="text-2xl font-bold mb-4">Kérjen ingyenes NIS2 konzultációt</h2>
        <p className="mb-6 text-purple-100">
          Adja meg elérhetőségeit és szakértő kollégánk hamarosan felveszi Önnel a kapcsolatot
        </p>
        
        {/* Jelentkezési űrlap */}
        <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Konzultációs igény beküldése</h3>
          
          {submitStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-100">
              Jelentkezését sikeresen rögzítettük! Hamarosan felvesszük Önnel a kapcsolatot.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-100">
              Hiba történt a jelentkezés során. Kérjük, próbálja újra később!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Kapcsolattartó neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-mail cím"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Cég neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefonszám"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Üzenet (opcionális)"
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full md:w-auto px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors duration-200 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Küldés...' : 'Konzultációt kérek'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default Contact; 